import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { latestVersions, templates, type FormTemplateSummary, type VisitNotice } from '../api/templates';
import { api } from '../api/client';
import type { VisitType } from '../api/types';
import {
  IconCheck,
  IconCircleAlert,
  IconLayers,
  IconPlus,
  IconRefreshCw,
  IconTrash2,
} from '../icons';
import { CODE_MAX, formatDate, tn } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge, type BadgeTone } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, TextInput } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { Table, type Column } from '../ui/Table';

/** Draft is in progress, published is in use, archived is finished with. */
const STATUS_TONE: Record<string, BadgeTone> = {
  Draft: 'neutral',
  Published: 'comment',
  Archived: 'na',
};

/** A date, or the open end of a range. */
const edge = (value: string | null) => (value ? formatDate(value) : '…');

export function Templates() {
  const t = useTranslate();
  const navigate = useNavigate();
  const [rows, setRows] = useState<FormTemplateSummary[]>([]);
  const [visitTypes, setVisitTypes] = useState<VisitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ code: '', name: '' });
  const [doomed, setDoomed] = useState<FormTemplateSummary | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, types] = await Promise.all([templates.list(), api.visitTypes.list()]);
      setRows(list);
      setVisitTypes(types);
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function create() {
    try {
      const detail = await templates.create({
        code: draft.code.trim(),
        name: draft.name.trim(),
        description: null,
        visitTypeId: visitTypes[0]?.id ?? null,
        checklistTitleId: null,
        countryIds: [],
        locationTypeIds: [],
        requiresSignature: false,
        notice: 'Announced' as VisitNotice,
        isScored: true,
        effectiveFrom: null,
        effectiveTo: null,
      });

      setCreating(false);
      setDraft({ code: '', name: '' });
      navigate(`/sablonlar/${detail.summary.id}`);
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
    }
  }

  async function remove(row: FormTemplateSummary) {
    try {
      await templates.remove(row.id);
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.deleteFailed'));
    }
  }

  // An older version stays Published so the audits started on it can finish; the badge says
  // it is no longer the one new work starts on (field test B-40).
  const latest = latestVersions(rows);

  const columns: Column<FormTemplateSummary>[] = [
    {
      id: 'name',
      header: t('templates.name'),
      className: 'is-primary',
      cell: (row) => (
        <span className="nv-badge-group">
          {row.name}
          <Badge tone="neutral" shape="square">{`v${row.version}`}</Badge>
        </span>
      ),
      skeletonWidth: '72%',
    },
    {
      id: 'visitType',
      header: t('templates.visitType'),
      className: 'is-muted',
      cell: (row) => row.visitTypeName ?? <span className="nv-subtle">{t('common.none')}</span>,
      skeletonWidth: '60%',
    },
    {
      id: 'scope',
      header: t('templates.scope'),
      cell: (row) => {
        const names = [...row.countryNames, ...row.locationTypeNames];

        // Nothing narrowed is a decision, not a gap, so it says so.
        if (names.length === 0) return <Badge tone="scale">{t('templates.everywhere')}</Badge>;

        return (
          <span className="nv-badge-group">
            {names.slice(0, 3).map((name) => (
              <Badge key={name} tone="neutral">{name}</Badge>
            ))}
            {names.length > 3 ? (
              <Badge title={names.slice(3).join(', ')}>{`+${names.length - 3}`}</Badge>
            ) : null}
          </span>
        );
      },
      skeletonWidth: '80%',
    },
    {
      id: 'notice',
      header: t('templates.notice'),
      className: 'is-muted',
      cell: (row) => t(`notice.${row.notice}`),
      skeletonWidth: '54px',
    },
    {
      id: 'scoring',
      header: t('templates.scoring'),
      className: 'is-muted',
      cell: (row) => t(row.isScored ? 'templates.scored' : 'templates.unscored'),
      skeletonWidth: '48px',
    },
    {
      id: 'status',
      header: t('templates.status'),
      cell: (row) => (
        <span className="nv-badge-group">
          <Badge
            tone={STATUS_TONE[row.status] ?? 'neutral'}
            title={row.status === 'Draft' ? t('templates.draftHint') : undefined}
          >
            {t(`formStatus.${row.status}`)}
          </Badge>
          {row.status === 'Published' && (latest.get(row.code) ?? 0) > row.version ? (
            <Badge tone="na" title={t('templates.supersededHint')}>
              {t('templates.superseded', { version: latest.get(row.code) ?? 0 })}
            </Badge>
          ) : null}
          {row.status === 'Published' && !row.isAvailableToday ? (
            <Badge tone="na" title={t('templates.outOfPeriodHint')}>{t('templates.outOfPeriod')}</Badge>
          ) : null}
        </span>
      ),
      skeletonWidth: '70px',
    },
    {
      id: 'period',
      header: t('templates.period'),
      className: 'is-muted',
      cell: (row) => `${edge(row.effectiveFrom)} → ${edge(row.effectiveTo)}`,
      skeletonWidth: '96px',
    },
    {
      id: 'questions',
      header: t('templates.questions'),
      align: 'right',
      cell: (row) => row.questionCount,
      skeletonWidth: '28px',
    },
  ];

  return (
    <>
      <PageHeader
        crumbs={[t('app.templates'), t('nav.templates')]}
        title={t('templates.title')}
        subtitle={t('templates.subtitle', { count: rows.length })}
        action={
          <Button variant="primary" icon={IconPlus} onClick={() => setCreating(true)}>
            {t('templates.add')}
          </Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-table-card">
        <Table
          caption={t('templates.title')}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          onRowClick={(row) => navigate(`/sablonlar/${row.id}`)}
          rowActions={(row) =>
            // Only a draft can be deleted; a published template is archived instead, which
            // the editor does, because audits already point at it.
            row.status === 'Draft' ? (
              <>
                {/* Opens the template rather than publishing from here, and says so with
                    the ellipsis. Publishing is refused until every category has questions
                    and the weights add up, and those reasons are listed in the editor — a
                    button here would either publish something nobody had looked at or
                    answer with a list of problems the list has no room to show.

                    It exists because the row said "Draft" and offered nothing to do about
                    it, so people went looking for the next step and did not find it. Twice
                    in one afternoon, once to the person who wrote this. */}
                <IconButton
                  label={t('templates.openToPublish')}
                  icon={IconCheck}
                  onClick={() => navigate(`/sablonlar/${row.id}`)}
                />
                <IconButton
                  label={t('common.delete')}
                  icon={IconTrash2}
                  tone="danger"
                  onClick={() => setDoomed(row)}
                />
              </>
            ) : null
          }
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            <EmptyState
              icon={IconLayers}
              title={t('templates.empty')}
              text={t('templates.emptyText')}
              action={
                <Button variant="primary" icon={IconPlus} onClick={() => setCreating(true)}>
                  {t('templates.add')}
                </Button>
              }
            />
          }
        />
      </Card>

      <Drawer
        open={creating}
        title={t('templates.newTitle')}
        onClose={() => setCreating(false)}
        footer={
          <>
            <Button onClick={() => setCreating(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!draft.code.trim() || !draft.name.trim()}
              onClick={() => void create()}
            >
              {t('templates.createAndOpen')}
            </Button>
          </>
        }
      >
        <Field label={t('templates.code')} hint={t('templates.codeHint')}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              placeholder="magaza-denetimi"
              maxLength={CODE_MAX}
              value={draft.code}
              onChange={(e) => setDraft({ ...draft, code: e.target.value })}
            />
          )}
        </Field>

        <Field label={t('templates.name')}>
          {({ id }) => (
            <TextInput id={id} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          )}
        </Field>
      </Drawer>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('common.deleteTitle', { name: doomed.name }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => (doomed ? remove(doomed) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        {doomed ? (
          <>
            <p>{tn('templates.deleteQuestions', doomed.questionCount)}</p>
            <p>{t('common.deleteWarning')}</p>
          </>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
